import { useEffect, useMemo, useRef, useState } from 'react'
import { DAY_CARD_DISPLAYS, showsNumbers, showsPicture, type DayCardDisplay } from '../../lib/dayCardDisplay'
import { createPortal } from 'react-dom'

import type { DayCardFilm, DayCardPreview, DaySection } from '../../api/types'
import { fehlertext } from '../../api/client'
import { useFilmLength, useSettings, useTrackSections, useZonenverlauf } from '../../hooks/queries'
import { useEscape } from '../../hooks/useEscape'
import { VehiclePicker } from '../../components/VehiclePicker'
import { SpeedPicker } from '../../components/SpeedPicker'
import { LoadingBar } from '../../components/LoadingBar'
import type { Vehicle } from '../../lib/vehicles'
import { ausFeld, uhrText, zeitText, zuFeld, type Uhr } from '../../lib/ortszeit'
import { landingChange, sectionZoneMarks, stadtVon, versatzIn } from '../../lib/zonen'
import { useTexte } from '../../texte'
import { AnimationFields, type AnimationValues } from '../settings/AnimationFields'
import { encodableFormat, filmFormat, filmSettingsFrom, profileCover, wishFrom, type FilmFormat, type FilmSettings } from './routeFilm'
import { filmScript } from '../../lib/filmScript'
import { TOUR_DEFAULTS, fieldsOfCard, filmOfCard, type TourChoice, type TourOptions } from '../../lib/tourOptions'
import { TourFields } from './TourFields'

/**
 * "Welcher Tag?" -- the question before a day card goes out (#79, 0.41.0).
 *
 * The server proposes the last day of the route that has points, today
 * included: whoever loads the recording in the evening and shares at once
 * gets today, whoever shares the next morning gets yesterday. The other
 * days of the route stand in the list, newest first, so that an older day
 * can be handed in later. A day without points is not in the list.
 *
 * **And since 0.42.0 "Von" and "Bis"** -- from use: "vielleicht will ich mal
 * nur einen Teil übernehmen." Both are prefilled with the first and the
 * last motion the server found for the day, in the trip's clock; whoever
 * wants a part of the day changes them. Choosing another day asks the
 * server for that day's motion and fills the fields again.
 *
 * **And since 1.96.0 the sections and the film** (#90). The sections of the
 * day stand with their vehicle, changeable here -- decided: "kann dann beim
 * Trennen oder spätestens beim Erstellen der Statistik geändert werden".
 * The change goes to the recording, as on the map, and every card of that
 * day reads it. Below them the animation, prefilled from the trip and
 * changeable for this one card. "Bis" before "Von" means the next day: a
 * night flight ends after midnight and still belongs to the day it began on.
 *
 * **And since 1.151.0 several days, "Nur Fahrt" and the rows.** From use:
 * "Auswahl mehrere (zusammenhängender Tage) von - bis" and "Zahlenkarte:
 * Auswahl ob die Routenabschnitte enthalten sind oder nicht". With a last
 * day, "Von" is a time of the first day and "Bis" one of the last (first
 * built without times, then from use: "auch mit Zeitauswahl für den ersten
 * und letzten Tag"); both are prefilled with the first and the last motion
 * of the span. "Nur Fahrt"
 * counts the distance by the rule of the time in motion, on for a new card;
 * the rows are off for a new card. Each of the three changes is a question
 * to the server, as another day is: the film's length is measured on the
 * day as it will be read.
 *
 * Built like `Rueckfrage`: on `document.body`, Escape and a tap on the
 * background say no, the safe way has the focus.
 */
export function Tagwahl({
  tripId,
  vorschau,
  versatz,
  animation,
  filmPossible,
  vorbelegt,
  keepable,
  holen,
  onWahl,
  onAbbruch,
}: {
  tripId: number
  vorschau: DayCardPreview
  versatz: Uhr
  /** The trip's settings for the film, the start for this card. */
  animation: AnimationValues
  /** Whether this browser can make a film (`canAnimate()`). */
  filmPossible: boolean
  /**
   * A card that is to be computed anew (1.86.0): its part of the day fills
   * "Von" and "Bis" instead of the day's first and last motion, and the
   * button says "Speichern".
   */
  vorbelegt?: { from: string | null; to: string | null; display: DayCardDisplay; film?: DayCardFilm | null; dayTo: string | null; movingOnly: boolean; showRows: boolean }
  /**
   * The card has a video that stays when nothing it was made from changed
   * (1.132.0); then "Video neu rechnen" stands in the sheet.
   */
  keepable?: boolean
  /** `from` and `to` cut the day (1.163.0): the part the film drives, for its length. */
  holen: (day: string, dayTo: string | undefined, movingOnly: boolean, from?: string, to?: string) => Promise<DayCardPreview>
  /** `tour` is the complex film's choice (#139), `null` for the simple film. */
  onWahl: (wahl: { day: string; from?: string; to?: string; dayTo?: string; movingOnly: boolean; showRows: boolean; film: FilmSettings | null; tour: TourChoice; stored: DayCardFilm; display: DayCardDisplay; rebuild: boolean }) => void
  onAbbruch: () => void
}) {
  const t = useTexte()
  const [tag, setTag] = useState(vorschau.day)
  /** The last day of several (1.151.0); empty for one day. */
  const [bis, setBis] = useState(vorbelegt?.dayTo ?? vorschau.day_to ?? '')
  const [nurFahrt, setNurFahrt] = useState(vorbelegt?.movingOnly ?? true)
  const [zeilen, setZeilen] = useState(vorbelegt?.showRows ?? false)
  const mehrereTage = bis !== ''
  /** The preview of the chosen day: its sections stand in the sheet. */
  const [current, setCurrent] = useState(vorschau)
  // The film as the card was made (1.125.0, "Neu erstellen"), otherwise the
  // trip's values and every switch off -- the film as it always was.
  const stored = vorbelegt?.film ? fieldsOfCard(vorbelegt.film) : null
  const [filmValues, setFilmValues] = useState(stored?.values ?? animation)
  const [tour, setTour] = useState<TourOptions>(stored?.tour ?? TOUR_DEFAULTS)
  const sections = useTrackSections(tripId)
  const zones = useZonenverlauf(tripId)
  const [start, setStart] = useState(zeit(vorbelegt?.from ?? vorschau.stats.started_at, versatz))
  const [ende, setEnde] = useState(zeit(vorbelegt?.to ?? vorschau.stats.ended_at, versatz))
  const [laedt, setLaedt] = useState(false)
  // What the card shows (#132): only numbers grey out the film, its length
  // and the speeds -- there is no picture for them to change.
  const [display, setDisplay] = useState<DayCardDisplay>(vorbelegt?.display ?? 'both')
  const [rebuild, setRebuild] = useState(false)
  const noPicture = !showsPicture(display)
  useEscape(onAbbruch)

  // Another day: its preview, and its first and last motion into the
  // fields. The answer of a day that is no longer chosen is dropped.
  //
  // **Only when the day changes** (1.110.1). From use, with a recording on
  // the iPad: a vehicle chosen here went back to the old one after a few
  // seconds. `holen` and `vorbelegt` come from the input line as a new
  // function and a new object every time it draws, and both stood in this
  // effect's list: every drawing of the input line put the sheet back to the
  // preview it opened with -- vehicles, speeds, "Von" and "Bis". They are
  // read through refs now; what is chosen in the sheet stays.
  const holenNow = useRef(holen)
  holenNow.current = holen
  const vorbelegtNow = useRef(vorbelegt)
  vorbelegtNow.current = vorbelegt
  /** The day as loaded; `current` is the same with the sheet's own changes. */
  const [loaded, setLoaded] = useState(vorschau)
  /** The part between "Von" and "Bis" as the server cut it (1.163.0), for the film's length. */
  const [cut, setCut] = useState<{ key: string; preview: DayCardPreview } | null>(null)
  useEffect(() => {
    // A part cut from another day is no length for this one (1.163.0).
    setCut(null)
    // The preview the sheet opened with is the day as read with its own
    // last day and counting; any other choice asks the server.
    if (tag === vorschau.day && bis === (vorschau.day_to ?? '') && nurFahrt === (vorschau.moving_only ?? false)) {
      setCurrent(vorschau)
      setLoaded(vorschau)
      return
    }
    let gilt = true
    setLaedt(true)
    holenNow.current(tag, bis === '' ? undefined : bis, nurFahrt)
      .then((p) => {
        if (!gilt) return
        setCurrent(p)
        setLoaded(p)
      })
      .catch(() => {
        if (gilt) {
          setStart('')
          setEnde('')
        }
      })
      .finally(() => {
        if (gilt) setLaedt(false)
      })
    return () => {
      gilt = false
    }
  }, [tag, bis, nurFahrt, vorschau])

  // The fields follow the loaded day and the zone (1.100.0: a new zone
  // course comes back as `versatz`), not the drawing of the input line.
  useEffect(() => {
    const own = loaded === vorschau ? vorbelegtNow.current : undefined
    setStart(zeit(own?.from ?? loaded.stats.started_at, versatz))
    setEnde(zeit(own?.to ?? loaded.stats.ended_at, versatz))
  }, [loaded, vorschau, versatz])

  // "Bis" at or before "Von": the next day (a night flight, #90).
  const nextDay = !mehrereTage && start !== '' && ende !== '' && ende <= start
  // The moments of "Von" and "Bis", as they go to the server.
  // Over several days (1.151.0) "Von" is on the first day and "Bis" on the last.
  const fromIso = start === '' ? undefined : ausFeld(`${tag}T${start}`, versatz)
  const toIso = ende === '' ? undefined : ausFeld(`${mehrereTage ? bis : nextDay ? dayAfter(tag) : tag}T${ende}`, versatz)

  // The part of the day the film drives (1.163.0). From use: "wenn
  // Parameter angepasst werden … wird die Zeit des Videos nicht neu
  // berechnet". The length stood on the whole day, and the film is made from
  // the part between "Von" and "Bis" -- the server cuts it
  // (`withSplitPoints()`), so it is asked here as well, a moment after the
  // last change of a field, and not cut a second time in the browser. The
  // whole day needs no question: its sections are the ones in the sheet.
  const whole = start === zeit(loaded.stats.started_at, versatz) && ende === zeit(loaded.stats.ended_at, versatz) && !nextDay
  const cutKey = whole || laedt ? null : JSON.stringify([tag, bis, nurFahrt, fromIso ?? null, toIso ?? null])
  useEffect(() => {
    if (cutKey === null) return
    let gilt = true
    const timer = setTimeout(() => {
      holenNow.current(tag, bis === '' ? undefined : bis, nurFahrt, fromIso, toIso)
        .then((p) => { if (gilt) setCut({ key: cutKey, preview: p }) })
        // Without an answer the length stays the one before; the card itself goes as asked.
        .catch(() => {})
    }, CUT_WAIT_MS)
    return () => {
      gilt = false
      clearTimeout(timer)
    }
  }, [cutKey, tag, bis, nurFahrt, fromIso, toIso])
  // The cut keeps what is chosen in this sheet: a vehicle or a speed changed
  // a moment ago may not be on the server yet. The list of sections shows the
  // same part (1.166.0, from use: "nur die relevanten Streckenabschnitte"),
  // with their times cut at "Von" and "Bis": a section outside the part does
  // nothing in the film, and a choice there changed nothing the sheet makes.
  // While a new part is asked, the one before stays, like the length.
  const cutNow = cutKey === null ? null : cut?.preview ?? null
  const partSections = useMemo(() => {
    const own = current.sections ?? []
    if (cutNow === null) return own
    return (cutNow.sections ?? []).map((s) => {
      const mine = own.find((o) => o.track_id === s.track_id && o.split_id === s.split_id)
      return mine === undefined ? s : { ...s, vehicle: mine.vehicle, speed: mine.speed }
    })
  }, [current, cutNow])
  const lengthPins = cutNow === null ? current.highlights ?? [] : cutNow.highlights ?? []
  /** The length of a part asked for and not yet answered: the number is the one before. */
  const lengthWaits = cutKey !== null && cut?.key !== cutKey

  // The days after the chosen one, oldest first: the choice for the last day.
  const spaetere = vorschau.days.filter((d) => d.day > tag).reverse()

  // A change of the zone course hours after a landing (1.100.0, from use:
  // "der Flug ist um ca. 11:00 Uhr Ortszeit angekommen und nicht um 17:00").
  // Proposed, not done: the course is the trip's, and moving an entry
  // computes anew what took its zone from it. The change goes the way the
  // trip's page takes (`useZonenverlauf`); the new course comes back as the
  // prop and the proposal goes.
  const landing = laedt ? null : landingChange(versatz, current.sections ?? [])
  const inNewZone = (iso: string) => (landing === null ? '' : uhrText(iso, versatzIn(landing.entry.tz_name, iso)))

  // What the film will last (1.104.0): a speed other than normal changes
  // the length, in both modes, and from use came "Hinweise, dass bei
  // Abweichungen von normal, sich die Filmlänge ändern kann". Computed from
  // the same script the film is made from, so the number is the film's; the
  // tile server's zoom only moves the camera, never the length.
  const settingsAnswer = useSettings().data
  const settings = settingsAnswer?.settings ?? null
  // The complex film's map (D): one of the fixed list, or the trip's (`null`).
  const tourSource = settingsAnswer?.map_fixed === true ? null : (settingsAnswer?.map_choices ?? []).find((c) => c.key === tour.map)?.map ?? null
  // The film's own format (1.107.0): the zoom levels a flight travels depend
  // on it -- **the one this device encodes** (1.112.0, review), not the one
  // wished for: a device without 1080p makes the film in 1280 x 720, and
  // the number here is the film's only with the same frame. Where nothing
  // encodes (this Chromium, `routefilm.mjs`, which hands the film its codecs
  // through a door) the wished format stands in: the number is then a
  // preview of a film the plus menu will decline, not nothing.
  const [frame, setFrame] = useState<FilmFormat | null>(null)
  useEffect(() => {
    if (!filmPossible) return
    let alive = true
    void encodableFormat(filmFormat(settings)).then((encodable) => { if (alive) setFrame(encodable?.format ?? filmFormat(settings)) })
    return () => { alive = false }
  }, [filmPossible, settings])
  // The bounds of the length come from the server (1.121.0); without them no film is offered.
  const lengthBounds = useFilmLength()
  // The length of the film, always and not only with a speed (1.149.0): the
  // stops and the flights change it too, and whether it fits its limit is
  // asked here (`FilmTiming`). From use: "falls der Film zu lang wird:
  // Abfrage: Film länger oder dynamische Anpassungen".
  const filmLength = useMemo(() => {
    const list = partSections
    if (!filmPossible || lengthBounds === null || frame === null || filmValues.daycard_anim !== 'on') return null
    // The same strip as the film (1.148.0): the flights are measured between the same views.
    const wish = wishFrom(filmSettingsFrom(filmValues, lengthBounds, tour.fit), null, tour.profile ? profileCover(frame) : 0)
    const withSpeeds = filmScript(list, lengthPins, frame, wish)
    const normal = filmScript(list.map((s) => ({ ...s, speed: 1 })), lengthPins, frame, wish)
    if (withSpeeds === null || normal === null) return null
    return { seconds: Math.round(withSpeeds.duration), normal: Math.round(normal.duration), timing: withSpeeds.timing }
  }, [partSections, lengthPins, filmPossible, filmValues, frame, lengthBounds, tour.profile, tour.fit])
  /** Too long, and not yet said what then: "Senden" waits for the answer. */
  const fitOpen = filmLength !== null && !noPicture && filmLength.timing.over && tour.fit === null

  function senden() {
    onWahl({
      day: tag,
      from: fromIso,
      to: toIso,
      dayTo: mehrereTage ? bis : undefined,
      movingOnly: nurFahrt,
      showRows: zeilen,
      film: filmPossible && lengthBounds !== null && filmValues.daycard_anim === 'on' && !noPicture ? filmSettingsFrom(filmValues, lengthBounds, tour.fit) : null,
      tour: { ...tour, source: tourSource },
      // What the card stores, so "Neu erstellen" shows it again (1.125.0).
      stored: filmOfCard(filmValues, tour),
      display,
      rebuild,
    })
  }

  // A vehicle or a speed changed here goes to the recording (1.98.1: the
  // speed too, from use). **It stands at once**: until then the buttons
  // were locked while the server saved and the whole day was read again,
  // and a tap in that time did nothing -- "anfangs muss man auch öfter
  // drücken". Nothing else of the day changes with a vehicle, so the day is
  // not asked again; a refusal puts the section back and says so below.
  // A time in another zone than the day's start says which (1.164.0), the rule of the card itself.
  function sectionTimes(from: string, to: string): string {
    const [zoneFrom, zoneTo] = sectionZoneMarks(versatz, current.stats.started_at, from, to)
    const inZone = (time: string, city: string | null) => (city === null ? time : t.tageskarte.timeInZone(time, city))
    return t.karte.sections.fromTo(inZone(zeitText(from, versatz), zoneFrom), inZone(zeitText(to, versatz), zoneTo))
  }

  function changeSection(section: DaySection, part: { vehicle?: Vehicle; speed?: number }) {
    const same = (s: DaySection) => s.track_id === section.track_id && s.split_id === section.split_id
    setCurrent((old) => ({ ...old, sections: old.sections.map((s) => (same(s) ? { ...s, ...part } : s)) }))
    sections.mutate(
      section.split_id === null
        ? { kind: 'first', trackId: section.track_id, ...part }
        : { kind: 'vehicle', trackId: section.track_id, splitId: section.split_id, ...part },
      {
        onError: () =>
          setCurrent((old) => ({
            ...old,
            sections: old.sections.map((s) => (same(s) ? { ...s, vehicle: section.vehicle, speed: section.speed } : s)),
          })),
      },
    )
  }

  return createPortal(
    <div className="rueckfrage" role="dialog" aria-modal="true" aria-label={t.chat.tagWelcher} onClick={onAbbruch} data-tagwahl="">
      <div className="rueckfrage__blatt" onClick={(event) => event.stopPropagation()}>
        <p className="rueckfrage__frage">{t.chat.tagWelcher}</p>
        <div className="bogen__rolle">
          <label className="field field--dicht">
            <span className="field__label">{t.chat.tagFeld}</span>
            <select
              className="field__input"
              value={tag}
              onChange={(event) => {
                setTag(event.target.value)
                // A last day before the first is no span: one day again.
                if (bis !== '' && bis <= event.target.value) setBis('')
              }}
              data-tagwahl-tag=""
            >
              {vorschau.days.map((d) => (
                <option key={d.day} value={d.day}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field field--dicht">
            <span className="field__label">{t.chat.tagBisTag}</span>
            <select className="field__input" value={bis} onChange={(event) => setBis(event.target.value)} disabled={spaetere.length === 0} data-tagwahl-bis-tag="">
              <option value="">{t.chat.tagNurDieser}</option>
              {spaetere.map((d) => (
                <option key={d.day} value={d.day}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
          <div className="tagwahl__zeiten">
            <label className="field field--dicht">
              <span className="field__label">{mehrereTage ? t.chat.tagVonErster : t.chat.tagVon}</span>
              <input className="field__input" type="time" value={start} onChange={(event) => setStart(event.target.value)} disabled={laedt} data-tagwahl-ab="" />
            </label>
            <label className="field field--dicht">
              <span className="field__label">
                {mehrereTage ? t.chat.tagBisLetzter : t.chat.tagBis}
                {nextDay && <span className="tagwahl__next-day" data-tagwahl-next-day="">{t.chat.dayNextDay}</span>}
              </span>
              <input className="field__input" type="time" value={ende} onChange={(event) => setEnde(event.target.value)} disabled={laedt} data-tagwahl-bis="" />
            </label>
          </div>
          <p className="leise">{mehrereTage ? t.chat.tagMehrereHinweis : t.chat.tagTeilHinweis}</p>
          <label className="kartenregler__nadel">
            <input type="checkbox" checked={nurFahrt} onChange={(e) => setNurFahrt(e.target.checked)} disabled={laedt} data-tagwahl-nur-fahrt="" />
            <span>{t.chat.tagNurFahrt}</span>
          </label>
          <p className="leise">{t.chat.tagNurFahrtHinweis}</p>
          <label className="field field--dicht">
            <span className="field__label">{t.chat.dayDisplay}</span>
            <select
              className="field__input"
              value={display}
              onChange={(event) => setDisplay(event.target.value as DayCardDisplay)}
              disabled={laedt}
              data-tagwahl-display=""
            >
              {DAY_CARD_DISPLAYS.map((d) => (
                <option key={d} value={d}>{t.chat.dayDisplayChoice[d]}</option>
              ))}
            </select>
          </label>
          {/* The rows only where the numbers stand (1.151.0). */}
          {showsNumbers({ display }) && (
            <label className="kartenregler__nadel">
              <input type="checkbox" checked={zeilen} onChange={(e) => setZeilen(e.target.checked)} data-tagwahl-zeilen="" />
              <span>{mehrereTage ? t.chat.tagZeilenTage : t.chat.tagZeilenAbschnitte}</span>
            </label>
          )}
          {/* While another day is read, a running bar stands where its sections
              will be (1.100.0, from use: "zumindest sollte dann ein
              Fortschrittsbalken angezeigt werden"). Until then the sections of
              the day before stood there, greyed, and nothing said that the
              sheet was waiting. */}
          {laedt && (
            <div className="tagwahl__sections" data-tagwahl-laedt="">
              <LoadingBar label={t.chat.tagLaedt} />
              <p className="leise">{t.chat.tagLaedt}</p>
            </div>
          )}
          {!laedt && partSections.length > 0 && (
            <div className="tagwahl__sections">
              <p className="field__label">{t.chat.daySections}</p>
              <ol className="sections">
                {partSections.map((section, i) => (
                  <li
                    key={`${section.track_id}/${section.split_id ?? 'first'}/${section.from}`}
                    className="sections__row"
                    data-day-section={i}
                    data-vehicle={section.vehicle}
                  >
                    <span className="sections__time">
                      {sectionTimes(section.from, section.to)}
                    </span>
                    <VehiclePicker
                      value={section.vehicle}
                      label={t.karte.sections.vehicleOf(i + 1)}
                      disabled={laedt}
                      onPick={(vehicle) => changeSection(section, { vehicle })}
                    />
                    <SpeedPicker
                      value={section.speed ?? 1}
                      sectionKey={section.split_id ?? 'first'}
                      disabled={laedt || noPicture}
                      onPick={(speed) => changeSection(section, { speed })}
                    />
                  </li>
                ))}
              </ol>
              {sections.isError && <p className="bogen__problem" role="alert">{fehlertext(sections.error)}</p>}
            </div>
          )}
          {landing !== null && (
            <div className="tagwahl__landing" data-landing-zone={landing.entry.id} data-landing-early={landing.early ? '' : undefined}>
              <p className="leise">
                {(landing.early ? t.chat.landingZoneEarlyHint : t.chat.landingZoneHint)(
                  stadtVon(landing.entry.tz_name),
                  inNewZone(landing.entry.valid_from!),
                  inNewZone(landing.landing),
                )}
              </p>
              <button
                type="button"
                className="button button--outline button--small"
                disabled={zones.aendern.isPending}
                onClick={() => zones.aendern.mutate({ id: landing.entry.id, valid_from: landing.landing })}
                data-schluessel="landing-zone"
              >
                {t.chat.landingZoneApply}
              </button>
              {zones.aendern.isError && <p className="bogen__problem" role="alert">{fehlertext(zones.aendern.error)}</p>}
            </div>
          )}
          {filmPossible ? (
            // A disabled fieldset greys every field inside it, the film's
            // switch and its length alike -- one line instead of a prop
            // threaded through `AnimationFields`.
            <fieldset className="tagwahl__film" disabled={noPicture} data-tagwahl-film="">
              <AnimationFields values={filmValues} onChange={(part) => setFilmValues((old) => ({ ...old, ...part }))} />
              {filmValues.daycard_anim === 'on' && (
                <TourFields options={tour} onChange={(part) => setTour((old) => ({ ...old, ...part }))} />
              )}
              {filmValues.daycard_anim === 'on' && keepable === true && (
                <label className="kartenregler__nadel">
                  <input type="checkbox" checked={rebuild} onChange={(e) => setRebuild(e.target.checked)} data-tour="rebuild" />
                  <span>{t.chat.tour.rebuild}</span>
                </label>
              )}
              {filmLength !== null && !noPicture && filmLength.timing.over && (
                <fieldset className="tagwahl__fit" data-film-fit={tour.fit ?? ''}>
                  <legend className="leise">
                    {filmValues.daycard_anim_mode === 'fixed'
                      ? t.chat.filmFit.overFixed(Math.round(filmLength.timing.natural), Math.round(filmLength.timing.limit))
                      : t.chat.filmFit.overMost(Math.round(filmLength.timing.natural), Math.round(filmLength.timing.limit))}
                  </legend>
                  <label className="kartenregler__nadel">
                    <input type="radio" name="film-fit" value="longer" checked={tour.fit === 'longer'} onChange={() => setTour((old) => ({ ...old, fit: 'longer' }))} data-film-fit-choice="longer" />
                    <span>{t.chat.filmFit.longer(Math.round(filmLength.timing.longer))}</span>
                  </label>
                  <label className="kartenregler__nadel">
                    <input type="radio" name="film-fit" value="fit" checked={tour.fit === 'fit'} onChange={() => setTour((old) => ({ ...old, fit: 'fit' }))} data-film-fit-choice="fit" />
                    <span>{t.chat.filmFit.fit(Math.round(filmLength.timing.limit))}</span>
                  </label>
                </fieldset>
              )}
            </fieldset>
          ) : (
            animation.daycard_anim === 'on' && <p className="leise" data-tagwahl-no-film="">{t.chat.dayFilmUnsupported}</p>
          )}
        </div>
        {/* The length at the foot, outside the scrolling part (1.163.0, from
            use: "die voraussichtliche Dauer immer unten angegeben"): a speed
            is chosen at the top and the fields of the film below, and the
            number stood after them. While "zu lang" is not answered, the
            line says so instead; the numbers stand at the question. */}
        {filmLength !== null && !noPicture && (
          <p
            className="leise tagwahl__dauer"
            role="status"
            data-film-length={filmLength.seconds}
            data-film-length-normal={filmLength.normal}
            data-film-length-waits={lengthWaits ? '' : undefined}
            data-film-fit-open={fitOpen ? '' : undefined}
          >
            {fitOpen ? t.chat.filmFit.choose : t.chat.dayFilmLength(filmLength.seconds, filmLength.normal)}
          </p>
        )}
        <div className="rueckfrage__knoepfe">
          <button type="button" className="button button--outline button--small" onClick={onAbbruch} autoFocus>
            {t.allgemein.abbrechen}
          </button>
          <button type="button" className="button button--small" onClick={senden} disabled={laedt || fitOpen} data-tagwahl-senden="">
            {vorbelegt === undefined ? t.chat.tagSenden : t.chat.tagNeuErstellen}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/**
 * The sheet before the days are known (1.100.0): it stands at the tap, with
 * a running bar, until the server has read the route.
 *
 * From use, on a route of a year: "Performanceprobleme … beim Aufruf bis die
 * Tagesauswahl kommt" -- and then "zumindest sollte dann ein
 * Fortschrittsbalken angezeigt werden". Until then the tap put one line under
 * the composer and the sheet came when the answer did; on a slow server that
 * looked like a tap that did nothing. Same place, same width, same title as
 * the sheet that follows, so that only its body changes. Escape, a tap on
 * the background and "Abbrechen" say no, as there.
 */
export function TagwahlWarten({ onAbbruch }: { onAbbruch: () => void }) {
  const t = useTexte()
  useEscape(onAbbruch)
  return createPortal(
    <div className="rueckfrage" role="dialog" aria-modal="true" aria-label={t.chat.tagWelcher} onClick={onAbbruch} data-tagwahl-wartet="">
      <div className="rueckfrage__blatt" onClick={(event) => event.stopPropagation()}>
        <p className="rueckfrage__frage">{t.chat.tagWelcher}</p>
        <LoadingBar label={t.chat.tagAuswerten} />
        <p className="leise">{t.chat.tagAuswerten}</p>
        <div className="rueckfrage__knoepfe">
          <button type="button" className="button button--outline button--small" onClick={onAbbruch} autoFocus>
            {t.allgemein.abbrechen}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/** The calendar day after `YYYY-MM-DD`. */
/** How long the sheet waits after the last change of "Von" or "Bis" before it asks for the part. */
const CUT_WAIT_MS = 400

function dayAfter(day: string): string {
  const d = new Date(`${day}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + 1)
  return d.toISOString().slice(0, 10)
}

/** "08:12" in the trip's clock for a `<input type="time">`, or empty. */
function zeit(iso: string | null, versatz: Uhr): string {
  if (iso === null) return ''
  return zuFeld(iso, versatz).slice(11, 16)
}
